import React, { useState, useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import Masonry from 'react-masonry-css'
import { Lightbox } from './Lightbox'

interface MasonryImage {
  id: string
  thumbnail: string
  fullsize: string
  galleryId: string
  galleryTitle: string
  keywords: string[]
}

interface MasonryGalleryProps {
  images: MasonryImage[]
  searchKeywords?: string[]
}

export const MasonryGallery: React.FC<MasonryGalleryProps> = ({ 
  images, 
  searchKeywords = [] 
}) => {
  const [lightboxImages, setLightboxImages] = useState<Array<{
    id: string,
    thumbnail: string,
    fullsize: string,
    title?: string,
    galleryTitle?: string
  }>>([])
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [visibleImages, setVisibleImages] = useState<MasonryImage[]>([])
  const [hasMore, setHasMore] = useState(true)
  
  const IMAGES_PER_LOAD = 20

  useEffect(() => {
    // Reset visible images when images prop changes
    setVisibleImages(images.slice(0, IMAGES_PER_LOAD))
    setHasMore(images.length > IMAGES_PER_LOAD)
  }, [images])

  const loadMoreImages = () => {
    const currentLength = visibleImages.length
    const moreImages = images.slice(currentLength, currentLength + IMAGES_PER_LOAD)
    setVisibleImages(prev => [...prev, ...moreImages])
    setHasMore(currentLength + IMAGES_PER_LOAD < images.length)
  }

  const openLightbox = (imageIndex: number) => {
    const lightboxImageArray = visibleImages.map(img => ({
      id: img.id,
      thumbnail: img.thumbnail,
      fullsize: img.fullsize,
      galleryTitle: img.galleryTitle
    }))
    setLightboxImages(lightboxImageArray)
    setLightboxIndex(imageIndex)
    setIsLightboxOpen(true)
  }

  const closeLightbox = () => {
    setIsLightboxOpen(false)
  }

  const navigateLightbox = (index: number) => {
    setLightboxIndex(index)
  }

  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 1
  }

  const highlightKeywords = (keywords: string[], searchTerms: string[]) => {
    if (searchTerms.length === 0) return []
    return keywords.filter(keyword => 
      searchTerms.some(term => keyword.toLowerCase().includes(term.toLowerCase()))
    )
  }

  return (
    <div className="w-full">
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="flex -ml-4 w-auto"
        columnClassName="pl-4 bg-clip-padding"
      >
        {visibleImages.map((image, index) => (
          <ImageItem
            key={image.id}
            image={image}
            index={index}
            onOpenLightbox={openLightbox}
            highlightedKeywords={highlightKeywords(image.keywords, searchKeywords)}
          />
        ))}
      </Masonry>

      {hasMore && (
        <InfiniteLoader onLoadMore={loadMoreImages} />
      )}

      <Lightbox
        images={lightboxImages}
        currentIndex={lightboxIndex}
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        onNavigate={navigateLightbox}
      />
    </div>
  )
}

interface ImageItemProps {
  image: MasonryImage
  index: number
  onOpenLightbox: (index: number) => void
  highlightedKeywords: string[]
}

const ImageItem: React.FC<ImageItemProps> = ({ 
  image, 
  index, 
  onOpenLightbox, 
  highlightedKeywords 
}) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  })
  const [isLoaded, setIsLoaded] = useState(false)
  const [aspectRatio, setAspectRatio] = useState(1)

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    setAspectRatio(img.naturalHeight / img.naturalWidth)
    setIsLoaded(true)
  }

  return (
    <div ref={ref} className="mb-4 group cursor-pointer">
      <div className="relative overflow-hidden bg-gray-100 rounded-sm">
        {inView && (
          <img
            src={image.thumbnail}
            alt={`From ${image.galleryTitle}`}
            className={`w-full h-auto object-cover transition-all duration-700 group-hover:scale-105 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              aspectRatio: aspectRatio || 'auto'
            }}
            onLoad={handleImageLoad}
            onClick={() => onOpenLightbox(index)}
            loading="lazy"
          />
        )}
        
        {!isLoaded && inView && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-pulse bg-gray-200 w-full h-full"></div>
          </div>
        )}
        
        {/* Overlay with gallery info on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-end">
          <div className="p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <p className="text-sm font-light">{image.galleryTitle}</p>
            {highlightedKeywords.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {highlightedKeywords.slice(0, 3).map((keyword, idx) => (
                  <span key={idx} className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                    {keyword}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const InfiniteLoader: React.FC<{ onLoadMore: () => void }> = ({ onLoadMore }) => {
  const { ref, inView } = useInView({
    threshold: 0.1
  })

  useEffect(() => {
    if (inView) {
      onLoadMore()
    }
  }, [inView, onLoadMore])

  return (
    <div ref={ref} className="flex justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
    </div>
  )
}
